/**
 * useActorChanges — лента заявок на изменение публичной карточки оператора
 * («Бот для клиентов»). Общий хук для OO и EO (perm actor_approval).
 *
 * Backend (staff-auth через api-хелпер):
 *  - GET  /api/oo/actor-changes?status=pending → { status, count, items:[FeedItem] }
 *  - GET  /api/oo/actor-changes/:id            → полный diff (было/станет) + payload + current
 *  - POST /api/oo/actor-changes/:id/approve         → { ok, status:"approved" } (409 если обработана)
 *  - POST /api/oo/actor-changes/:id/reject          body { note }
 *  - POST /api/oo/actor-changes/:id/needs-revision  body { note }
 *
 * Лента группируется по оператору в компоненте; здесь — плоский список + детальная
 * загрузка по id + три действия с reload. UTC+5 даты приходят строками с бэка.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";

export type ChangeStatus = "pending" | "approved" | "rejected" | "needs_revision";

/** Лёгкая запись ленты (GET actor-changes). */
export interface ActorChangeFeedItem {
  id: number;
  staffId: string;
  /** Ник оператора (для группировки/заголовка). */
  operator: string;
  status: ChangeStatus;
  /** Краткая сводка изменений («сменил фото профиля», «новая новость», …). */
  summary: string[];
  submittedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
}

/** Одно поле diff: было/станет. */
export interface DiffField {
  before: unknown;
  after: unknown;
}

/** Полная заявка (GET actor-changes/:id) с diff + payload + текущей карточкой. */
export interface ActorChangeDetail {
  id: number;
  staffId: string;
  operator: string;
  status: ChangeStatus;
  submittedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  payload: { profile?: Record<string, unknown>; news?: Array<Record<string, unknown>> };
  current: Record<string, unknown> | null;
  diff: Record<string, DiffField>;
}

interface FeedResponse {
  status?: string;
  count?: number;
  items?: ActorChangeFeedItem[];
}

export interface UseActorChangesResult {
  items: ActorChangeFeedItem[];
  count: number;
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** Подгрузить полный diff заявки. */
  fetchDetail: (id: number) => Promise<ActorChangeDetail | null>;
  approve: (id: number) => Promise<{ ok: boolean; error?: string }>;
  reject: (id: number, note: string) => Promise<{ ok: boolean; error?: string }>;
  needsRevision: (id: number, note: string) => Promise<{ ok: boolean; error?: string }>;
}

function errMsg(e: unknown): string {
  if (e && typeof e === "object" && "status" in e) {
    const s = (e as { status?: number }).status;
    if (s === 409) return "Заявка уже обработана";
  }
  return e instanceof Error ? e.message : "Ошибка";
}

export function useActorChanges(): UseActorChangesResult {
  const [items, setItems] = useState<ActorChangeFeedItem[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const abortRef = useRef<AbortController | null>(null);
  const reload = useCallback(() => setTrigger((n) => n + 1), []);

  useEffect(() => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await api<FeedResponse>("/api/oo/actor-changes?status=pending", { signal: ac.signal });
        if (ac.signal.aborted) return;
        setItems(r.items ?? []);
        setCount(r.count ?? (r.items?.length ?? 0));
        setLoading(false);
      } catch (e: unknown) {
        if (ac.signal.aborted) return;
        setItems([]);
        setCount(0);
        setError(errMsg(e));
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [trigger]);

  const fetchDetail = useCallback(async (id: number): Promise<ActorChangeDetail | null> => {
    try {
      return await api<ActorChangeDetail>(`/api/oo/actor-changes/${id}`);
    } catch {
      return null;
    }
  }, []);

  const approve = useCallback(async (id: number) => {
    try {
      await api(`/api/oo/actor-changes/${id}/approve`, { method: "POST" });
      reload();
      return { ok: true };
    } catch (e: unknown) {
      return { ok: false, error: errMsg(e) };
    }
  }, [reload]);

  const reject = useCallback(async (id: number, note: string) => {
    try {
      await api(`/api/oo/actor-changes/${id}/reject`, { method: "POST", body: { note } });
      reload();
      return { ok: true };
    } catch (e: unknown) {
      return { ok: false, error: errMsg(e) };
    }
  }, [reload]);

  const needsRevision = useCallback(async (id: number, note: string) => {
    try {
      await api(`/api/oo/actor-changes/${id}/needs-revision`, { method: "POST", body: { note } });
      reload();
      return { ok: true };
    } catch (e: unknown) {
      return { ok: false, error: errMsg(e) };
    }
  }, [reload]);

  return { items, count, loading, error, reload, fetchDetail, approve, reject, needsRevision };
}
